import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
type GenerationMode = 'default' | 'analyzing' | 'generating';
interface ProgramGenerationProps {
  onComplete: () => void;
  mode?: GenerationMode;
}
const modeContent: Record<GenerationMode, {
  title: string;
  body: string;
  helpers: string[];
}> = {
  default: {
    title: 'Creating your personalized program...',
    body: 'Aligning your goals, schedule, and experience.',
    helpers: []
  },
  analyzing: {
    title: 'Building your next program',
    body: "We're reviewing your workout history and current preferences to recommend the best structure for your next phase.",
    helpers: ['Checking your recent training patterns', 'Matching your goal and training frequency', 'Preparing your next program structure']
  },
  generating: {
    title: 'Creating your program',
    body: "We're building your workout days and exercise plan for your selected structure.",
    helpers: ['Organizing your weekly training split', 'Preparing your workout day lists', 'Finalizing your next program version']
  }
};
export function ProgramGeneration({
  onComplete,
  mode = 'default'
}: ProgramGenerationProps) {
  const [helperIndex, setHelperIndex] = useState(0);
  const content = modeContent[mode];
  useEffect(() => {
    const timer = setTimeout(() => {
      onComplete();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onComplete]);
  useEffect(() => {
    if (content.helpers.length === 0) return;
    const interval = setInterval(() => {
      setHelperIndex(prev => (prev + 1) % content.helpers.length);
    }, 1200);
    return () => clearInterval(interval);
  }, [content.helpers.length]);
  return <div className="flex flex-col items-center justify-center h-full w-full bg-stone-50 px-6 relative overflow-hidden" data-id="element-1030">
      {/* Soft background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-emerald-200/30 rounded-full blur-3xl animate-pulse" data-id="element-1031" />

      <div className="relative z-10 flex flex-col items-center" data-id="element-1032">
        <div className="w-40 h-40 rounded-[2.5rem] overflow-hidden border-4 border-white shadow-xl bg-stone-200 mb-8 relative" data-id="element-1033">
          <video autoPlay loop muted playsInline className="w-full h-full object-cover" src="https://cdn.magicpatterns.com/uploads/69mUG6ooMsKbP89Ta37mkt/Katethought_friendly_gym_mascot_character_funny_good_guy_musc_bc3cfd10-111a-4766-8e76-81af2852edc8_2.mp4" data-id="element-1034" />
        </div>

        <div className="flex flex-col items-center space-y-4 text-center" data-id="element-1035">
          <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" data-id="element-1036" />
          <h2 className="text-2xl font-extrabold text-slate-800 tracking-tight" data-id="element-1037">
            {content.title}
          </h2>
          <p className="text-slate-500 font-medium leading-relaxed max-w-xs" data-id="element-1038">
            {content.body}
          </p>
          {content.helpers.length > 0 && <p className="text-xs font-bold text-emerald-500 uppercase tracking-wider animate-in fade-in duration-300" key={helperIndex} data-id="element-1039">
              {content.helpers[helperIndex]}
            </p>}
        </div>
      </div>
    </div>;
}